import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useTranslation } from '@/i18n'
import { Eye, FileText, Loader2, Search, Table2, Workflow } from 'lucide-react'
import { toast } from './toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { apiService, type ConnectCandidate } from '@/service/api'
import { cn } from '@/lib/utils'
import SourceFilePreviewOverlay from './SourceFilePreviewOverlay'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import { escapeBelongsToInput } from '@/components/previews/escapeBelongsToInput'

/** The file the eye opened, and what it takes to show it well. */
interface PreviewTarget {
  id: string
  name: string
  /**
   * The candidate's own tag. A sheet lands on its column; a document searches
   * for it — it is what the reviewer opened the file to check.
   */
  tag: string
  /**
   * The graph document behind a drawing, when the candidate's file is one.
   *
   * The board's preview opens a drawing as this graph, the way a board P&ID
   * card holds it (Original | Detected | Review); the drawing's own id would
   * need resolving to it.
   */
  graphId: string | null
  /**
   * The tag's own node on that drawing, so the editor opens zoomed onto the
   * tag — the landing the tag page's board gives a sheet. Null when the
   * candidate is not a drawing, or the registry has no node for it there.
   */
  nodeId?: string | null
}

/**
 * The preview, as a modal dialog of its own, stacked over Edit Mapping.
 *
 * It used to be a plain child of this dialog's `DialogContent`, and that is why
 * the eye's overlay came up SMALL while the Mapped-tag column's came up full
 * screen: `DialogContent` centres itself with a CSS `translate`, and a
 * transformed ancestor is the containing block for `position: fixed`, so the
 * overlay's `inset-0` and the shared panel's `100%` resolved against the 800px
 * dialog card, not the viewport. Measured in Chromium at 1440x900: the panel
 * was 750x397 inside the dialog against 1392x800 from the table.
 *
 * A portal to `document.body` escapes the transform, but a bare portal is
 * OUTSIDE the modal for Radix's purposes: clicks on it dismiss Edit Mapping,
 * its focus trap pulls focus back, and `aria-hidden` on everything outside the
 * dialog hides the preview from assistive tech. Nesting a second Radix dialog
 * is the supported answer to all three at once — layers stack, so Escape and
 * outside-clicks go to the top one, focus moves into it and back out, and the
 * hidden-others bookkeeping is Radix's own. The overlay inside draws its own
 * scrim and panel; this element only hosts it at the viewport.
 */
const PreviewLayer = ({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) => (
  <DialogPrimitive.Root
    open
    onOpenChange={(next) => {
      if (!next) onClose()
    }}
  >
    <DialogPrimitive.Portal>
      <DialogPrimitive.Content
        className="fixed inset-0 z-[9999999]"
        // The overlay is the whole description; a hidden title names the file.
        aria-describedby={undefined}
        // Radix hands Escape to this top layer. A search box inside the panel
        // owns its own Escape, so the layer stays up for that press.
        onEscapeKeyDown={(e) => {
          if (escapeBelongsToInput(e)) e.preventDefault()
        }}
        data-testid="edit-mapping-preview-layer"
      >
        <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
)

/**
 * The row being re-pointed. Deliberately structural rather than one of the two
 * row types: Matched and Pending describe the same thing here -- a P&ID tag,
 * the document it was read off, and how to show it -- and taking the union
 * would make every caller's shape this dialog's business.
 */
export interface EditMappingTarget {
  /** The RAW parse content -- the identity the match row is keyed on. */
  tag: string
  /** The readable device, for the title. Falls back to `tag`. */
  displayTag?: string
  /** The P&ID the tag was read off, carried through to the write. */
  documentId?: string | null
  /**
   * Whether the row had a mapping to replace. `create` is a Pending row with
   * no proposal at all -- its menu says Map, and the dialog it opens said
   * "Edit Mapping" over a tag that had never been mapped (Tag Mapping review
   * 2026-09-28, F29). Only the title reads it: the write is the same connect.
   * Absent means `edit`, so every existing caller keeps its title.
   */
  mode?: 'create' | 'edit'
  /**
   * The mapping this Edit REPLACES: the Matched row's own match. Sent as
   * `replaces_match_id`, so the server retires it in the same request as the
   * new one — Edit used to add a second mapping beside the old one (Tag Mapping
   * review 2026-09-28, F34). Nothing about the dialog changes; only what the
   * write does. Absent on Map from Pending, where there is nothing to replace.
   */
  replaces?: {
    matchId: string
  }
}

interface Props {
  target: EditMappingTarget | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fired after a successful write so the caller can refresh its list. */
  onMapped: (target: EditMappingTarget, picked: ConnectCandidate) => void
}

/**
 * The type glyph beside a source name. `source_type` is free text from the
 * document row, so this maps what we see and falls through to a document icon
 * rather than rendering nothing for an unrecognised kind.
 */
/**
 * The picker's grid: 294 / 294 / 144 of the frame's 768px table as ratios, plus
 * the fixed 36px preview column (1232:285585). `minmax(0,…)` on the three
 * flexible ones is what lets `truncate` shrink a long source name instead of
 * widening the dialog.
 */
const COLS =
  'grid-cols-[minmax(0,294fr)_minmax(0,294fr)_minmax(0,144fr)_36px]'

/**
 * Where a candidate's source file can actually be viewed.
 *
 * `/document/:id` is the PDF viewer. Connect candidates are drawn from
 * `timeseries_tags`, so most of them are spreadsheets and JSON exports, and
 * sending those to the PDF viewer renders a blank pane -- the common case, not
 * an edge one.
 *
 * A SUBSET of the Data Catalog's own dispatch, deliberately: that one keys off
 * `doc.properties` (`db_path`) and a `ConnectCandidate` carries no properties,
 * only the type and the name. So this decides on the type plus the extension
 * and falls back to the document viewer, which is where the catalog also lands
 * when its discriminators miss. `pnid`/`pfd` would want the graph editor, but
 * that needs `properties` too -- and a drawing cannot be a timeseries-tag
 * candidate in the first place.
 */
const viewerPathFor = (candidate: ConnectCandidate): string => {
  const kind = (candidate.source_type ?? '').toLowerCase()
  const ext = (candidate.document_name ?? '').toLowerCase().split('.').pop() ?? ''
  const id = candidate.document_id
  if (kind === 'live_stream') return `/live-stream/${id}`
  if (kind === 'datasheet' || kind === 'table') return `/datasheet/${id}`
  if (kind === 'timeseries') {
    if (['xlsx', 'xls', 'csv'].includes(ext)) return `/datasheet/${id}`
    // Anything else (a connector's extensionless "<source> — <label>" name
    // included) is not a PDF: a candidate has no stored path to inspect.
    return `/timeseries/${id}`
  }
  return `/document/${id}`
}

/**
 * Whether the overlay can show this candidate.
 *
 * Figma 13 draws the preview as an overlay over the dialog. The overlay
 * renders a page — a PDF or an image — and, since it mounts the sheet viewer,
 * a spreadsheet too. A JSON timeseries and a live stream still go to the
 * viewer built for them rather than to an overlay that would render "not
 * available" for them (Abdullah, #34136).
 */
const previewsInOverlay = (candidate: ConnectCandidate): boolean => {
  const path = viewerPathFor(candidate)
  return path.startsWith('/document/') || path.startsWith('/datasheet/')
}

const iconFor = (sourceType?: string | null) => {
  const kind = (sourceType ?? '').toLowerCase()
  if (kind.includes('timeseries') || kind.includes('time_series')) return Table2
  if (kind.includes('pnid') || kind.includes('p&id') || kind.includes('pfd')) return Workflow
  return FileText
}

/**
 * "Edit Mapping" -- re-point a P&ID tag at a different source tag.
 *
 * Restores the workflow #32653 removed with the Pending actions column, to the
 * shape Figma 1232:284987 draws for it: a three-column picker (Tag, Source
 * name, Source type) rather than the single-column list the old Connect dialog
 * used. The endpoints never went anywhere, so this is a UI change only.
 *
 * Built on the raw ui/dialog rather than IntuiDialog because the list has to
 * scroll inside a fixed-height body with the actions pinned below it -- the
 * same FS-534 layout contract the old dialog documented.
 */
const EditMappingDialog = ({ target, open, onOpenChange, onMapped }: Props) => {
  /** The file the eye opened, over the dialog. Null when nothing is previewing. */
  const [preview, setPreview] = useState<PreviewTarget | null>(null)
  const userRole = useAppStore((st) => st.user?.role)
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [candidates, setCandidates] = useState<ConnectCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<ConnectCandidate | null>(null)
  const [saving, setSaving] = useState(false)
  /** The option holding the listbox's single tab stop. */
  const [active, setActive] = useState(0)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  // Reset per opening so one row's selection can never be submitted against
  // another -- the dialog is mounted once and reused for every row.
  useEffect(() => {
    if (!open) return
    setSearch('')
    setDebouncedSearch('')
    setSelected(null)
  }, [open, target?.tag])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!open || !target) return
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        // Deliberately NOT scoped to the target's document id. That id is the
        // P&ID the tag was READ OFF; the candidates live in the historian/xlsx
        // documents, so filtering by it returns an empty list every time. This
        // was found by driving the real page once already ("No candidate tags
        // found" on every row) -- do not add it back as a "fix".
        const resp = await apiService.getConnectCandidates({
          tag: target.tag,
          search: debouncedSearch || undefined,
        })
        if (cancelled) return
        setCandidates(resp.candidates)
        // A new list: the old index could point past the end, or at a
        // different candidate than the one the reader was on.
        setActive(0)
      } catch {
        if (cancelled) return
        setCandidates([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, target, debouncedSearch])

  /**
   * `source_type` as a label, through the catalog's own file-type vocabulary —
   * the same helper both tables use, so "Time Series" here and in the row the
   * picker was opened from cannot read differently.
   */
  const sourceTypeLabel = (type?: string | null): string => {
    if (!type) return '—'
    const key: string = `data_catalog:doc_types.${type.toLowerCase()}`
    return t(key, { defaultValue: type })
  }

  const description = useMemo(() => {
    if (!target) return ''
    return t('data_catalog:tag_mapping.edit_dialog.description', {
      defaultValue: 'Select tag from files that will be mapped to P&ID Tag "{{tag}}"',
      tag: target.displayTag || target.tag,
    })
  }, [target, t])

  const handleMap = useCallback(async () => {
    if (!target || !selected) return
    setSaving(true)
    try {
      const response = await apiService.connectTag({
        pid_tag: target.tag,
        ts_tag: selected.tag,
        pid_document_id: target.documentId ?? undefined,
        // Edit re-points the mapping: the server rejects this match once the
        // new one is saved, so the old row does not stay beside it (F34).
        replaces_match_id: target.replaces?.matchId,
      })
      // Say "replaced" only when the server says it retired THAT match. A
      // backend that predates `replaces_match_id` ignores the field and saves
      // the new mapping beside the old one; reporting that as a replacement
      // would be the F34 bug with a reassuring toast on top (Greptile, #35128).
      const replaced = !!target.replaces && response.replaced_match_id === target.replaces.matchId
      if (target.replaces && !replaced) {
        toast.error(
          t('data_catalog:tag_mapping.edit_dialog.not_replaced', {
            defaultValue: 'New mapping added, but the previous one could not be replaced.',
          }),
        )
      } else {
        toast.success(
          t('data_catalog:tag_mapping.edit_dialog.success', {
            defaultValue: 'Mapping updated successfully',
          }),
        )
      }
      onMapped(target, selected)
      onOpenChange(false)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('data_catalog:tag_mapping.edit_dialog.failed', {
              defaultValue: 'Could not update the mapping.',
            }),
      )
    } finally {
      setSaving(false)
    }
  }, [target, selected, onMapped, onOpenChange, t])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] p-0 gap-0">
        <DialogHeader className="p-4 pb-2 space-y-1 text-start">
          <DialogTitle className="text-base font-medium">
            {target?.mode === 'create'
              ? t('data_catalog:tag_mapping.edit_dialog.title_create', { defaultValue: 'Map tag' })
              : t('data_catalog:tag_mapping.edit_dialog.title', { defaultValue: 'Edit Mapping' })}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: DialogContent is a CSS grid and a grid item defaults to
            `min-width: auto`, so a long source name widens the whole dialog
            instead of truncating inside its column. */}
        <div className="flex min-h-[360px] max-h-[60vh] min-w-0 flex-col px-4">
          <div className="relative shrink-0">
            <Search className="absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <IntuiInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('data_catalog:tag_mapping.edit_dialog.search_placeholder', {
                defaultValue: 'Search',
              })}
              className="h-9 ps-9"
            />
          </div>

          {/* 294 / 294 / 144 / 36 of the frame's 768px table (1232:285585).
              The fourth is the per-row preview, and is the one fixed width:
              it holds a 20px glyph and must not stretch. */}
          <div
            className={`mt-3 grid shrink-0 items-center ${COLS} gap-2 border-b border-border px-2 py-3 text-sm text-muted-foreground`}
          >
            <span>{t('data_catalog:tag_mapping.edit_dialog.col_tag', { defaultValue: 'Tag' })}</span>
            <span>
              {t('data_catalog:tag_mapping.edit_dialog.col_source_name', {
                defaultValue: 'Source name',
              })}
            </span>
            <span>
              {t('data_catalog:tag_mapping.edit_dialog.col_source_type', {
                defaultValue: 'Source type',
              })}
            </span>
            {/* Unlabelled in the frame. */}
            <span className="sr-only">
              {t('data_catalog:tag_mapping.edit_dialog.col_preview', {
                defaultValue: 'Preview',
              })}
            </span>
          </div>

          {/* A listbox is a COMPOSITE control: one tab stop, arrows to move
              within it. Every option being independently tabbable meant
              tabbing through 50 candidates to reach Cancel, and the arrow keys
              did nothing -- which is the half-built state a reader of the
              markup would least expect. */}
          <div
            role="listbox"
            aria-label={t('data_catalog:tag_mapping.edit_dialog.candidates', {
              defaultValue: 'Candidate tags',
            })}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto"
            onKeyDown={(e) => {
              if (!candidates.length) return
              const delta =
                e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
              let next = active
              if (delta !== 0) {
                next = Math.min(candidates.length - 1, Math.max(0, active + delta))
              } else if (e.key === 'Home') {
                next = 0
              } else if (e.key === 'End') {
                next = candidates.length - 1
              } else {
                return
              }
              // Claim the key before the scroll container does, so arrowing
              // through the list does not also scroll the dialog body.
              e.preventDefault()
              setActive(next)
              optionRefs.current[next]?.focus()
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t('data_catalog:tag_mapping.edit_dialog.empty', {
                  defaultValue: 'No candidate tags found.',
                })}
              </div>
            ) : (
              candidates.map((candidate, index) => {
                const Icon = iconFor(candidate.source_type)
                const isSelected =
                  selected?.tag === candidate.tag &&
                  selected?.document_id === candidate.document_id
                return (
                  // A `div` with `role="option"`, not a `<button>`: the frame
                  // puts a preview control INSIDE the row, and a button inside
                  // a button is invalid HTML. The listbox pattern keeps the row
                  // reachable and operable from the keyboard.
                  <div
                    key={`${candidate.tag}-${candidate.document_id ?? ''}`}
                    ref={(node) => {
                      optionRefs.current[index] = node
                    }}
                    role="option"
                    // Roving: exactly one option is in the tab order.
                    tabIndex={index === active ? 0 : -1}
                    aria-selected={isSelected}
                    onFocus={() => setActive(index)}
                    onClick={() => setSelected(candidate)}
                    onKeyDown={(e) => {
                      // ONLY when the row itself has focus. The preview button
                      // is a child, so without this guard the row swallowed its
                      // Enter/Space: previewing by keyboard selected the row
                      // (enabling Map) and never opened anything.
                      if (e.target !== e.currentTarget) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelected(candidate)
                      }
                    }}
                    className={cn(
                      // min-w-0 on the row AND its cells: `truncate` cannot
                      // shrink a grid item below its content width without it.
                      'grid w-full min-w-0 cursor-pointer items-center gap-2 border-b border-border px-2 py-3 text-start text-sm',
                      COLS,
                      isSelected
                        // badge/info-background (#eff6ff) — the frame tints the
                        // picked row blue, not grey: grey is what its HOVER is.
                        ? 'bg-blue-50 font-medium text-foreground dark:bg-blue-900/20'
                        : 'text-foreground hover:bg-muted/50',
                    )}
                  >
                    <span className="min-w-0 truncate">{candidate.tag}</span>
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 truncate">{candidate.document_name ?? '—'}</span>
                    </span>
                    <span className="min-w-0 truncate text-muted-foreground">
                      {sourceTypeLabel(candidate.source_type)}
                    </span>
                    {/* Preview the file this tag was read from, WITHOUT losing
                        the dialog and the selection in it — hence a new tab.
                        Absent rather than inert when the candidate carries no
                        document id: there is nothing to open. */}
                    {candidate.document_id ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          // The row is the select target; the eye is not.
                          e.stopPropagation()
                          if (previewsInOverlay(candidate)) {
                            // A sheet's viewer sits behind AnnotatorRestrictedRoute
                            // on its own route; the board's preview has no route
                            // to guard, so the guard is here (Abdullah, #34397).
                            if (
                              isAnnotatorRole(userRole) &&
                              viewerPathFor(candidate).startsWith('/datasheet/')
                            ) {
                              notifyAnnotatorBlocked()
                              return
                            }
                            // An OVERLAY, over the dialog, keeping the dialog
                            // and the selection in it — Figma 13 (1320:209572).
                            // The graph id rides along so Detected can be the
                            // ANNOTATED drawing, not a flat page.
                            setPreview({
                              id: candidate.document_id as string,
                              name: candidate.document_name ?? candidate.tag,
                              tag: candidate.tag,
                              graphId: candidate.graph_id ?? null,
                              // And the tag's node on it, so the editor opens
                              // zoomed onto the tag — the same landing the
                              // tag page's board gives a sheet.
                              nodeId: candidate.node_id ?? null,
                            })
                            return
                          }
                          window.open(viewerPathFor(candidate), '_blank', 'noopener')
                        }}
                        aria-label={t('data_catalog:tag_mapping.edit_dialog.preview', {
                          defaultValue: 'Preview {{name}}',
                          name: candidate.document_name ?? candidate.tag,
                        })}
                        className="flex size-5 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                      >
                        <Eye className="size-5" aria-hidden />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        <DialogFooter className="items-center justify-between border-t border-border bg-muted/50 p-4 sm:justify-between">
          {/* The frame reads "Showing 10 of 1203". The endpoint returns a ranked
              page with no total, so this states what it can stand behind rather
              than inventing a denominator. */}
          <span className="text-xs text-muted-foreground">
            {t('data_catalog:tag_mapping.edit_dialog.showing', {
              defaultValue: 'Showing {{count}}',
              count: candidates.length,
            })}
          </span>
          <span className="flex items-center gap-2">
            <IntuiButton variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
              {t('data_catalog:tag_mapping.edit_dialog.cancel', { defaultValue: 'Cancel' })}
            </IntuiButton>
            <IntuiButton size="lg" onClick={handleMap} disabled={!selected || saving} loading={saving}>
              {t('data_catalog:tag_mapping.edit_dialog.map', { defaultValue: 'Map' })}
            </IntuiButton>
          </span>
        </DialogFooter>

        {/* Over the dialog, not instead of it: the selection the reviewer is
            making survives the look. See `PreviewLayer` for why it is a dialog
            of its own rather than a child of this one. */}
        {preview && (
          <PreviewLayer title={preview.name} onClose={() => setPreview(null)}>
            {/* The BOARD's preview (Pegah, 2026-10-13 review: every preview
                looks like the board's). A drawing opens as its graph with the
                tag's node boxed; any other file lands on the tag. */}
            {preview.graphId ? (
              <SourceFilePreviewOverlay
                documentId={preview.graphId}
                name={preview.name}
                highlightNodeId={preview.nodeId}
                onClose={() => setPreview(null)}
              />
            ) : (
              <SourceFilePreviewOverlay
                documentId={preview.id}
                name={preview.name}
                highlightTag={preview.tag}
                onClose={() => setPreview(null)}
              />
            )}
          </PreviewLayer>
        )}
      </DialogContent>

    </Dialog>
  )
}

export default EditMappingDialog
